import { API } from './api';
import { getFirstTouchAttribution } from './attribution';

const SESSION_KEY = 'speeddate_analytics_session_v1';
const ONCE_PREFIX = 'speeddate_analytics_once_v1';
const ERROR_TRACKING_FLAG = '__speeddateFirstPartyErrorTracking';

export const FIRST_PARTY_EVENTS = new Set([
  'landing_view',
  'registration_cta_click',
  'registration_form_view',
  'registration_start',
  'registration_form_submitted',
  'email_code_sent',
  'registration_confirmed',
  'frontend_error',
  'registration_field_focus',
  'registration_field_complete',
  'registration_submit_click',
  'registration_validation_error',
  'age_checkbox_checked',
  'submit_visible',
]);

export const REGISTRATION_FIELD_NAMES = new Set([
  'name',
  'email',
  'password',
  'age_checkbox',
]);

export const REGISTRATION_VALIDATION_CODES = new Set([
  'name_missing',
  'email_invalid',
  'password_short',
  'age_checkbox_missing',
]);

const FIELD_EVENTS = new Set([
  'registration_field_focus',
  'registration_field_complete',
]);

const VALIDATION_FIELD = {
  name_missing: 'name',
  email_invalid: 'email',
  password_short: 'password',
  age_checkbox_missing: 'age_checkbox',
};

const safeStorageGet = (storage, key) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeStorageSet = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const createUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const getAnonymousSessionId = (
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
) => {
  const existing = safeStorageGet(storage, SESSION_KEY);
  if (/^[0-9a-f-]{36}$/i.test(existing || '')) return existing;
  const created = createUuid();
  safeStorageSet(storage, SESSION_KEY, created);
  return created;
};

const detectBrowser = (userAgent) => {
  if (/Edg\//.test(userAgent)) return 'edge';
  if (/OPR\//.test(userAgent)) return 'opera';
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/CriOS\//.test(userAgent)) return 'chrome_ios';
  if (/FxiOS\//.test(userAgent)) return 'firefox_ios';
  if (/Chrome\//.test(userAgent)) return 'chrome';
  if (/Safari\//.test(userAgent)) return 'safari';
  return 'other';
};

const detectOs = (userAgent) => {
  if (/Android/.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (/Windows/.test(userAgent)) return 'windows';
  if (/Mac OS X/.test(userAgent)) return 'macos';
  if (/Linux/.test(userAgent)) return 'linux';
  return 'other';
};

const detectDevice = (userAgent, viewportWidth) => {
  if (/iPad|Tablet/.test(userAgent)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/.test(userAgent) || viewportWidth < 768) return 'mobile';
  return 'desktop';
};

const numericId = (value) => (/^\d{1,20}$/.test(String(value || '')) ? String(value) : null);

const normalizePathname = (pathname) => {
  const safePath = String(pathname || '/').slice(0, 200);
  const normalized = safePath.split('/').map((segment) => {
    if (
      /^[0-9a-f]{24}$/i.test(segment) ||
      /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(segment) ||
      /^\d{7,}$/.test(segment)
    ) {
      return '_id';
    }
    return /^[A-Za-z0-9_.-]*$/.test(segment) ? segment : '_segment';
  }).join('/');
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

export const buildFirstPartyPayload = ({
  eventName,
  eventId = createUuid(),
  errorType,
  component,
  errorCode,
  fieldName,
  validationCode,
} = {}) => {
  if (!FIRST_PARTY_EVENTS.has(eventName)) return null;
  if (FIELD_EVENTS.has(eventName) && !REGISTRATION_FIELD_NAMES.has(fieldName)) return null;
  if (
    eventName === 'registration_validation_error'
    && (
      !REGISTRATION_VALIDATION_CODES.has(validationCode)
      || VALIDATION_FIELD[validationCode] !== fieldName
    )
  ) return null;
  if (
    eventName === 'age_checkbox_checked'
    && fieldName !== 'age_checkbox'
  ) return null;
  const metadataEvent = FIELD_EVENTS.has(eventName)
    || eventName === 'registration_validation_error'
    || eventName === 'age_checkbox_checked';
  if (!metadataEvent && (fieldName || validationCode)) return null;
  const attribution = getFirstTouchAttribution() || {};
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const isSystemVk = attribution.utm_source === 'vk_ads';
  const payload = {
    event_name: eventName,
    event_id: eventId,
    timestamp: new Date().toISOString(),
    session_id: getAnonymousSessionId(),
    device: detectDevice(userAgent, viewportWidth),
    os: detectOs(userAgent),
    browser: detectBrowser(userAgent),
    viewport: `${Math.max(0, viewportWidth)}x${Math.max(0, viewportHeight)}`,
    pathname: normalizePathname(typeof window !== 'undefined' ? window.location.pathname : '/'),
    utm_source: attribution.utm_source || null,
    utm_medium: attribution.utm_medium || null,
    utm_campaign: attribution.utm_campaign || null,
    utm_content: attribution.utm_content || null,
    utm_term: attribution.utm_term || null,
    yclid: attribution.yclid || null,
    rb_clickid: attribution.rb_clickid || null,
    campaign_id: isSystemVk ? numericId(attribution.utm_campaign) : null,
    banner_id: isSystemVk ? numericId(attribution.utm_content) : null,
  };

  if (eventName === 'frontend_error') {
    payload.error_type = errorType;
    payload.component = component;
    payload.error_code = errorCode;
  }
  if (metadataEvent) payload.field_name = fieldName;
  if (eventName === 'registration_validation_error') {
    payload.validation_code = validationCode;
  }
  return payload;
};

const sendPayload = async (payload, fetchImpl = typeof fetch === 'function' ? fetch : null) => {
  if (!payload || !fetchImpl) return false;
  try {
    const response = await fetchImpl(`${API}/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch {
    return false;
  }
};

export const trackFirstPartyEvent = (eventName, options = {}) => {
  const payload = buildFirstPartyPayload({ eventName, ...options });
  if (!payload) return false;
  void sendPayload(payload);
  return true;
};

export const trackFirstPartyEventOnce = (
  eventName,
  occurrenceKey,
  options = {},
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
) => {
  if (!FIRST_PARTY_EVENTS.has(eventName) || !/^[A-Za-z0-9:/_.-]{1,160}$/.test(String(occurrenceKey || ''))) {
    return false;
  }
  const key = `${ONCE_PREFIX}:${eventName}:${occurrenceKey}`;
  const existingEventId = safeStorageGet(storage, key);
  if (existingEventId) return false;
  const eventId = createUuid();
  if (!safeStorageSet(storage, key, eventId)) return false;
  const payload = buildFirstPartyPayload({ eventName, eventId, ...options });
  if (!payload) return false;
  void sendPayload(payload);
  return true;
};

export const classifyRequestError = (error) => {
  if (error?.code === 'ERR_NETWORK') return 'network_error';
  const status = Number(error?.response?.status || 0);
  if (status >= 500) return 'server_error';
  if (status >= 400) return 'validation_error';
  return 'unexpected_error';
};

export const trackFrontendError = ({ errorType, component, errorCode }) =>
  trackFirstPartyEvent('frontend_error', { errorType, component, errorCode });

export const initFirstPartyAnalytics = () => {
  if (typeof window === 'undefined' || window[ERROR_TRACKING_FLAG]) return;
  window[ERROR_TRACKING_FLAG] = true;
  window.addEventListener('error', () => {
    trackFrontendError({
      errorType: 'uncaught_error',
      component: 'window',
      errorCode: 'window_error',
    });
  });
  window.addEventListener('unhandledrejection', () => {
    trackFrontendError({
      errorType: 'unhandled_rejection',
      component: 'window',
      errorCode: 'promise_rejection',
    });
  });
};
